import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, FAQ, CTABand } from "@/components/site/Blocks";
import { buildHead } from "@/lib/seo-head";

const faqItems = [
  {
    q: "¿Qué es un préstamo DSCR?",
    a: "DSCR significa Debt Service Coverage Ratio (cobertura del servicio de la deuda). Es un préstamo para propiedades de inversión en el que la calificación se basa principalmente en el flujo de caja de la propiedad (renta menos PITIA estimado), no en los ingresos personales del prestatario. Los programas DSCR elegibles no requieren declaraciones de impuestos, W-2s ni talones de pago.",
  },
  {
    q: "¿Puedo cerrar a nombre de una LLC?",
    a: "Sí. En los programas con fines comerciales para inversionistas, la vestimenta en LLC, LP, S-Corp, C-Corp y Series LLC es comúnmente aceptada, a menudo sin prima de tasa adicional. El prestatario personal o la entidad sigue pasando por revisión de crédito y verificación de reservas.",
  },
  {
    q: "¿Cuál es el máximo LTV?",
    a: "Los préstamos DSCR elegibles ofrecen hasta 80% LTV para compras y refinanciaciones de propiedades de inversión no ocupadas por el propietario, sujeto a crédito, reservas, tipo de propiedad, renta documentada y aprobación de suscripción. La mejoría y reinversión (fix & flip) y los préstamos de construcción suelen tener límites más bajos.",
  },
  {
    q: "¿Trabajan con extranjeros no residentes?",
    a: "Sí. Ofrecemos programas para inversionistas extranjeros (Foreign National) en propiedades de inversión con fines comerciales, generalmente con documentación de pasaporte, visa si aplica, carta de referencia bancaria internacional y reservas verificables.",
  },
  {
    q: "¿Puedo financiar una propiedad de Airbnb o alquiler a corto plazo?",
    a: "Sí. Los préstamos STR (Short-Term Rental) califican con proyección de ingresos de plataformas como Airbnb y Vrbo, sujeto a restricciones de ubicación, experiencia del prestatario y reservas. Consulte la página del programa STR para los límites actuales.",
  },
  {
    q: "¿Qué es un préstamo Fix & Flip?",
    a: "Es un préstamo a corto plazo para la compra y rehabilitación de propiedades de inversión, con liberación de fondos para la mejoría según el progreso. Suele requerir experiencia previa, análisis de desempeño del After Repair Value (ARV) y reservas de capital.",
  },
  {
    q: "¿Tienen préstamos para construcción de cero?",
    a: "Sí. Ofrecemos préstamos de construcción para inversionistas con planes de ocupación no propietaria, desembolsos por hitos según inspecciones y conversión a préstamo permanente al finalizar la construcción.",
  },
  {
    q: "¿Cómo funciona un préstamo Section 8 para inversionistas?",
    a: "Los préstamos para propiedades con subsidio Section 8 pueden calificar usando el ingreso por contrato de arrendamiento (incluyendo la porción HAP del gobierno) o una versión conservadora solo-HAP, según la pauta del programa. El ingreso del inquilino sigue siendo la base de calificación; no se requieren ingresos personales.",
  },
  {
    q: "¿Cuánto tiempo tarda la aprobación?",
    a: "La revisión inicial del escenario generalmente ocurre el mismo día hábil una vez recibida una presentación completa, autorización de crédito y documentación de respaldo. La aprobación formal, la cotización definitiva y el cierre dependen del prestamista mayorista, el avalúo, el título y otros factores.",
  },
  {
    q: "¿Son un prestamista directo?",
    a: "No. Somos un corredor hipotecario. Empaquetamos su escenario y lo colocamos con uno de nuestros socios prestamistas mayoristas. Ese prestamista suscribe, aprueba y financia el préstamo. Nuestro rol es estructurar el archivo, comparar opciones y coordinar el cierre.",
  },
];

export const Route = createFileRoute("/es/preguntas-frecuentes")({
  head: () =>
    buildHead({
      title: "Preguntas Frecuentes sobre Préstamos DSCR | Simply Approved Mortgages",
      description:
        "Respuestas claras sobre préstamos DSCR, LLCs, extranjeros, STR, fix & flip, construcción y Section 8 para inversionistas de propiedades de inversión.",
      url: "/es/preguntas-frecuentes",
      locale: "es",
      keywords:
        "preguntas frecuentes DSCR, qué es DSCR, préstamos LLC, préstamos extranjeros, préstamos STR, préstamos Airbnb, préstamos fix and flip, préstamos construcción, préstamos Section 8, corredor hipotecario inversionista",
      breadcrumbs: [
        { name: "Inicio", url: "/es" },
        { name: "Preguntas frecuentes", url: "/es/preguntas-frecuentes" },
      ],
      service: { name: "Préstamos DSCR y con fines comerciales" },
      faqs: faqItems,
    }),
  component: FAQPage,
});

function FAQPage() {
  return (
    <SiteShell>
      <PageHero eyebrow="Preguntas frecuentes" title="Preguntas frecuentes sobre préstamos DSCR y para inversionistas." />
      <Section>
        <div className="mx-auto max-w-4xl">
          <FAQ items={faqItems} />
          <div className="mt-10 rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
            <p>
              ¿No encontró su pregunta?{" "}
              <Link to="/es/contacto" className="font-semibold text-brand hover:underline">Envíenos un mensaje</Link>{" "}
              o llámenos al{" "}
              <a href="tel:+18778137219" className="font-semibold text-brand hover:underline">(877) 813-7219</a>.
            </p>
          </div>
        </div>
      </Section>
      <CTABand title="¿Listo para revisar su escenario?" body="Envíenos su escenario. Revisión inicial generalmente el mismo día hábil." />
    </SiteShell>
  );
}
